<template>
  <div>
    <div v-if="displayLoading">
      <Loading />
    </div>

    <div class="page">


      <div v-if="getModuleConfig('validate_page.step_section.enable_step_section')" class="p-3">
          <SharedStep :steps="getModuleConfig('validate_page.step_section.steps')" />
        </div>


      <div class="page__title">
        {{ getModuleConfig("validate_page.form_title") }}
      </div>
      <div class="page__desc">
        {{ getModuleConfig("validate_page.form_desc") }}
      </div>



      <div class="page__desc__text">
        {{ trans('sms_code_send_to', '簡訊驗證碼已發送至') }}<b class="google-sans-code">{{ mobile }}</b>，<br />
        <div v-if="counter">
          <div v-html="counterMessage"></div>
        </div>
        <div v-else>
          {{ trans('expired_sms_code', '驗證碼已失效，請重新驗證。') }}
        </div>
      </div>

      <div class="code-input">
        <CodeInput
          ref="codeInput"
          :key="codeInputKey"
          :loading="false"
          :fields="verifyCodeLength"
          :fieldHeight="80"
          class="google-sans-code"
          :class="{ invalid: !isValid }"
          @change="onChange"
        />
        <div v-if="!isValid" class="form__error-msg">
          {{ trans('wrong_sms_code', '驗證碼有誤，請再次確認') }}
        </div>
      </div>

      <div class="page__bottom">
        {{ trans('not_receive', '沒收到嗎？') }}<span @click="handleResend">{{ trans('send_sms_code_again', '重新發送驗證碼') }}</span>
      </div>
      <div class="page__bottom">
        {{ trans('wrong_mobile_phone', '手機號碼錯誤？') }}<span @click="handleReturn">{{ trans('refill_data', '重新填寫資料') }}</span>
      </div>
    </div>
    <div class="page__button">
      <SharedButton
        class="s-btn-bg-primary"
        type="button"
        @click="handleSubmit"
      >
        {{ getModuleConfig("validate_page.button_text") || '送出' }}
      </SharedButton>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/Page/Liff/Shared/Loading";
import CodeInput from "@/components/CodeInput";
import registerApi from "@/apis/liff/v2/register";
import SharedButton from "@/components/Page/Liff/Shared/Button";
import registerMixin from "@/mixins/liff/register";
import SharedStep from "@/components/Page/Liff/Shared/Steps";
import { mapActions } from "vuex";

export default {
  mixins: [registerMixin],
  components: {
    CodeInput,
    Loading,
    SharedButton,
    SharedStep
  },

  data() {
    return {
      displayLoading: false,
      verificationCode: null,
      counter: 120,
      ttl: 120,
      timer: null,
      isValid: true,
      codeInputKey: 0,
    };
  },

  computed: {
    verifyCodeLength() {
      const length = parseInt(this.getModuleConfig("verify_mobile.verify_code_length"), 10);
      return !isNaN(length) && length > 0 ? length : 6;
    },
    timeout() {
      return {
        minute: Math.floor(this.counter / 60),
        second: Math.floor(this.counter % 60),
      };
    },
    mobile() {
      const mobile = this.$store.state.liffAuth.user.mobile;
      return mobile
        ? mobile.substring(0, 3) + "****" + mobile.substring(7, 10)
        : null;
    },
    timeString() {
      let timeString = "";
      if (this.timeout.minute) timeString += `${this.timeout.minute} ${this.trans('minute', '分')} `;
      if (this.timeout.second) timeString += `${this.timeout.second} ${this.trans('second', '秒')}`;
      return timeString;
    },
    counterMessage() {
      const defaultTemplate = [
        '驗證碼將',
        this.timeout.minute > 0 ? ` <b>{minute}</b> 分` : '',
        this.timeout.second > 0 ? ` <b>{second}</b> 秒` : '',
        '後失效'
      ].join('')

      return this.trans('sms_code_expire_message', defaultTemplate, {
        minute: this.timeout.minute,
        second: this.timeout.second,
      })
    },
  },

  beforeDestroy() {
    this.clearTimer();
  },

  async mounted() {
    await this.initVerifyCodeCounter();
    this.startTimer();
  },

  methods: {
    ...mapActions("liffAuth", ["fetchMe", "setBindingErrorMessage"]),

    async initVerifyCodeCounter() {
      const defaultTtl = this.getModuleConfig("verify_mobile.retry_gap_seconds") || 120;
      this.ttl = defaultTtl;

      try {
        const mobilePhone = this.$store.state.liffAuth.user.mobile;
        const response = await registerApi.getVerifyCodeExpiredTime(mobilePhone);
        const expiredAt = response?.data?.expired_at;

        if (!expiredAt) {
          this.counter = 0;
          return;
        }

        const now = Math.floor(Date.now() / 1000);
        const remainSeconds = Math.max(0, expiredAt - now + 1);
        this.counter = remainSeconds;
      } catch (error) {
        this.counter = this.ttl;
      }
    },

    onChange(e) {
      this.verificationCode = e;
    },

    async handleResend() {
      this.displayLoading = true;
      this.isValid = true;
      this.clearCodeInput();

      try {
        await registerApi.resendPhone();
        await this.initVerifyCodeCounter();
        this.restartTimer();
        this.$swal(
          this.trans('success', "成功"),
          this.trans('sent_sms', '簡訊驗證碼已送出'),
          "success"
        );
      } catch (error) {
        // undefined 錯誤也會顯示
        const errorMessage = error.response?.data?.message
          ?? error.message
          ?? "發生例外錯誤";

        if (error.response?.data?.status === "failed") {
          const expiredAt = error.response.data?.expired_at;
          if (expiredAt) {
            this.counter =
              expiredAt -
              Math.floor(Date.now() / 1000) +
              1;
            this.$swal(
              this.trans('please_wait', '請稍候'),
              this.trans('wait_a_minute_and_click_again', '請於 {timeString} 後，再次點選「重新發送驗證碼」', {
                timeString: this.timeString,
              }),
              "error"
            );
          } else {
            this.$swal(this.trans('error', '錯誤'), errorMessage, 'error');
          }
        } else {
          // 包含網路錯誤、後端例外等所有其他錯誤情況
          this.$swal(this.trans('error', '錯誤'), errorMessage, 'error');
        }
        this.restartTimer();
      }

      this.displayLoading = false;
    },

    async handleSubmit() {
      if (!this.verificationCode || this.verificationCode.length !== this.verifyCodeLength) {
        this.isValid = false;
        return;
      }

      this.displayLoading = true;

      try {
        await registerApi.verifyPhone(this.verificationCode);
      } catch (error) {
        if (error.response.data.code === '4001') {
          this.isValid = false;
        } else {
          this.$swal(
            this.trans('error', '錯誤'),
            error.response.data.message ?? "發生錯誤，請重新確認",
            'error'
          );
        }
        this.displayLoading = false;
        return;
      }

      try {
        const response = await registerApi.proceedRegistration(this.$route.query.from);

        if (response.data.status == 'success' && response.data.next) {
          this.goNext(response.data.next)
          return
        }

        if (response.data.status == 'error' && response.data.next) {
          this.setBindingErrorMessage(response?.data?.reason);
          this.goNext(response.data.next)
          return;
        }
      } catch (error) {
        this.setBindingErrorMessage(error.response.data.message);
        this.goNext('binding_error')
        this.displayLoading = false;
        return;
      }

      // status is success, but no next
      await this.fetchMe();
      this.$router.push({
        name: "LiffRegisterWelcome",
        params: {
          orgCode: this.$route.params.orgCode,
        },
      });
      this.displayLoading = false;
    },

    startTimer() {
      this.timer = window.setInterval(() => {
        this.counter--;

        if (this.counter > 0) {
          return;
        }

        this.clearTimer();
      }, 1000);
    },
    clearTimer() {
      if (!this.timer) {
        return;
      }

      window.clearInterval(this.timer);
      this.timer = null;
    },
    restartTimer() {
      this.clearTimer();
      this.startTimer();
    },
    async handleReturn() {
      this.displayLoading = true;
      try {
        await registerApi.cleanMobilePhone();
        this.$router.push({
          name: "LiffRegisterMember",
          params: {
            orgCode: this.$route.params.orgCode,
          },
        });
      } catch (error) {
        console.log("error", error);
        this.displayLoading = false;
      }
    },
    clearCodeInput() {
      this.verificationCode = '';
      this.codeInputKey++;
      this.$nextTick(() => {
        this.$refs.codeInput?.$el?.querySelector('input')?.focus()
      })
    }
  },
};
</script>

<style lang="scss" scoped>
.form__error-msg {
  color: #fe0000;
  font-size: 12px;
  margin-top: 8px;
  margin-left: calc((100% - 60px * 5 - 8px * 4) / 2);
}
.code-input {
  margin-bottom: 32px;
  margin-top: 20px;

  ::v-deep .react-code-input-container {
    margin: auto;
    text-align: center;

    &.invalid input {
      border: 1px solid #fe0000;
    }
  }
  ::v-deep input {
    border: 1px solid #636366 !important;
    border-radius: 10px !important;
    font-size: 50px;
    font-weight: 600;
    font-family: PingFang TC;
    color: var(--primary-color) !important;

    &:not(:last-of-type) {
      margin-right: 8px;
    }

    &:focus {
      border: 1px solid var(--liff-primary_text_color);
      caret-color: var(--liff-primary_text_color);
    }
    &:focus + input {
      border: 1px solid #636366;
    }
  }
}
.page__bottom {
  font-size: 14px;
  line-height: 22px;
  color: var(--liff-primary_text_color);
  text-align: center;
  span {
    text-decoration: underline;
    cursor: pointer;
    color: var(--primary-color) !important;
  }
}
.page__desc__text {
  text-align: center;
  color: var(--liff-primary_text_color) !important;
  b {
    color: var(--primary-color) !important;
  }
}
.page__desc {
  color: var(--liff-secondary_text_color);
}
</style>
